import { Icon } from '@iconify/react'
import { useState } from 'react'
import { useNavigate } from 'react-router'
import { toast } from 'react-toastify'
import ConfirmActionModal from '../../components/ConfirmActionModal'
import { useAuth } from '../../hooks/useAuth'
import Dashboard from '../../layout/Dashboard'
import { userService } from '../../services/UserService'
import { NavContainer, NavWrapper } from '../../styles/CrypoAssetStyle'
import { Content, Heading } from '../../styles/OverviewStyle'

export default function SettingsGeneral(): React.JSX.Element {
   const [confirmPasswordReset, setConfirmPasswordReset] = useState(false)
   const [confirmPinReset, setConfirmPinReset] = useState(false)
   const { user, isSuperAdmin } = useAuth()
   const navigate = useNavigate()

   const resetPassword = async () => {
      if (!user?.id) return
      try {
         const response = await userService.resetAdminPassword(user.id)
         if (response.status === 'success') {
            toast.success(response.message || 'Password reset successfully')
         } else {
            toast.error(response.message || 'Failed to reset password')
         }
      } catch (error) {
         toast.error(error instanceof Error ? error.message : 'Failed to reset password')
      }
      setConfirmPasswordReset(false)
   }

   const resetPin = async () => {
      if (!user?.id) return
      try {
         const response = await userService.resetAdminPin(user.id)
         if (response.status === 'success') {
            toast.success(response.message || 'PIN reset successfully')
         } else {
            toast.error(response.message || 'Failed to reset PIN')
         }
      } catch (error) {
         toast.error(error instanceof Error ? error.message : 'Failed to reset PIN')
      }
      setConfirmPinReset(false)
   }

   const getRoleBadge = (role: string) => {
      const roleMap: Record<string, { label: string; color: string; bg: string }> = {
         superadmin: { label: 'Super-Admin', color: '#761BB8', bg: 'rgba(118,27,184,0.08)' },
         admin: { label: 'Admin', color: '#AF7AD5', bg: 'rgba(175,122,213,0.08)' }
      }
      const roleStyle = roleMap[role.toLowerCase()] || roleMap.admin!
      return (
         <span className="px-3 py-1 rounded-full text-[12px] font-geist-mono font-medium" style={{ backgroundColor: roleStyle.bg, color: roleStyle.color }}>
            {roleStyle.label}
         </span>
      )
   }

   return (
      <Dashboard title="Settings / General" icon="solar:settings-linear" active="settings">
         <Content>
            <div className="flex items-center justify-between mb-2">
               <Heading>Settings</Heading>
               <div />
            </div>


            <NavContainer>
               <NavWrapper children="General" $active />
               {isSuperAdmin && <NavWrapper children="Admin" onClick={() => navigate('/settings/admin')} />}
            </NavContainer>

            <div className="mt-4 divide-y divide-[#F1F1F3] border-t border-[#F1F1F3]">
               {/* Role */}
               <div className="grid grid-cols-[280px_1fr_auto] items-center py-4">
                  <div className="flex items-center gap-2 text-[#9C9C9F]"><Icon icon="solar:shield-user-linear" width={16} /> Role</div>
                  <div>{user?.role && getRoleBadge(user.role)}</div>
                  <div></div>
               </div>

               {/* Email */}
               <div className="grid grid-cols-[280px_1fr_auto] items-center py-4">
                  <div className="flex items-center gap-2 text-[#9C9C9F]"><Icon icon="fontisto:email" width={15} /> Email</div>
                  <div className="text-sm text-[#56565C]">{user?.emailAddress}</div>
                  <div></div>
               </div>

               {/* Password */}
               <div className="grid grid-cols-[280px_1fr_auto] items-center py-4">
                  <div className="flex items-center gap-2 text-[#9C9C9F]"><Icon icon="solar:lock-keyhole-linear" width={16} /> Password</div>
                  <div className="tracking-[0.2em] text-[#9C9C9F]">•••••••</div>
                  {isSuperAdmin && <button onClick={() => setConfirmPasswordReset(true)} className="text-sm text-[#6D6D72] hover:underline">Reset</button>}
               </div>

               {/* Admin pin */}
               <div className="grid grid-cols-[280px_1fr_auto] items-center py-4">
                  <div className="flex items-center gap-2 text-[#9C9C9F]"><Icon icon="solar:key-minimalistic-linear" width={16} /> Admin pin</div>
                  <div className="tracking-[0.2em] text-[#9C9C9F]">••••</div>
                  {isSuperAdmin && <button onClick={() => setConfirmPinReset(true)} className="text-sm text-[#6D6D72] hover:underline">Reset</button>}
               </div>
            </div>

            <ConfirmActionModal
               open={confirmPasswordReset}
               title="Reset Password"
               message="Are you sure you want to reset your password? You will receive instructions to set a new password."
               confirmText="Reset"
               onClose={() => setConfirmPasswordReset(false)}
               onConfirm={resetPassword}
            />

            <ConfirmActionModal
               open={confirmPinReset}
               title="Reset PIN"
               message="Are you sure you want to reset your admin PIN? You will need to set up a new PIN."
               confirmText="Reset"
               onClose={() => setConfirmPinReset(false)}
               onConfirm={resetPin}
            />
         </Content>
      </Dashboard>
   )
}
